/**
 * Loco Dropdown — custom select component
 *
 * Usage:
 *   {% render 'loco-dropdown', id: '...', label: '...', options_html: captured_options, variant: 'text' %}
 *   Include loco-dropdown.css + loco-dropdown.js on the page (see snippets/loco-dropdown-assets.liquid)
 *
 * API:
 *   LocoDropdown.init(scope?)  — init all [data-loco-dropdown] in scope
 *   LocoDropdown.initOne(el)   — init a single root element
 *   LocoDropdown.syncFromNative(scope?) — sync trigger/panel/disabled state from native <select>
 */
(function () {
  const ROOT_SELECTOR = "[data-loco-dropdown]";

  function closeAll(except) {
    document.querySelectorAll(`${ROOT_SELECTOR}.is-open`).forEach((root) => {
      if (root === except) return;

      root.classList.remove("is-open");
      root.querySelector(".loco-dropdown__trigger")?.setAttribute("aria-expanded", "false");

      const panel = root.querySelector(".loco-dropdown__panel");
      if (panel) panel.hidden = true;

      root.querySelector(".loco-dropdown__arrow")?.classList.remove("is-open");
    });
  }

  let globalsBound = false;

  function bindGlobals() {
    if (globalsBound) return;
    globalsBound = true;

    document.addEventListener("click", () => closeAll());
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeAll();
    });
  }

  function syncOptionDisabledState(optionButton, option) {
    const isDisabled = Boolean(option.disabled);
    optionButton.classList.toggle("is-disabled", isDisabled);
    optionButton.setAttribute("aria-disabled", isDisabled ? "true" : "false");
    optionButton.disabled = isDisabled;
  }

  function findNextEnabledIndex(select, fromIndex, delta) {
    let index = fromIndex + delta;

    while (index >= 0 && index < select.options.length) {
      if (!select.options[index].disabled) return index;
      index += delta;
    }

    return fromIndex;
  }

  function initOne(root) {
    if (root.dataset.locoDropdownInit === "true") return;

    const select = root.querySelector(".loco-dropdown__native");
    if (!select) return;

    root.dataset.locoDropdownInit = "true";

    const navigateOnSelect = root.dataset.locoDropdownNavigate === "true";
    const swatchMode = root.dataset.locoDropdownSwatchMode || "default";
    const isMetaSwatch = swatchMode === "meta";

    const hasSwatches = Array.from(select.options).some(
      (option) => option.dataset.swatchColor || option.dataset.swatchImage
    );

    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "loco-dropdown__trigger";
    trigger.setAttribute("aria-haspopup", "listbox");
    trigger.setAttribute("aria-expanded", "false");

    Array.from(root.children).forEach((child) => {
      if (child !== select) trigger.appendChild(child);
    });

    root.appendChild(trigger);

    select.classList.add("loco-dropdown__native--hidden");
    select.tabIndex = -1;
    select.setAttribute("aria-hidden", "true");

    const panel = document.createElement("div");
    panel.className = "loco-dropdown__panel";
    panel.hidden = true;
    panel.setAttribute("role", "listbox");

    const list = document.createElement("ul");
    list.className = "loco-dropdown__list";

    Array.from(select.options).forEach((option, index) => {
      const item = document.createElement("li");
      item.className = "loco-dropdown__item";

      const optionButton = document.createElement("button");
      optionButton.type = "button";
      optionButton.className = "loco-dropdown__option";
      optionButton.setAttribute("role", "option");
      optionButton.dataset.index = String(index);

      if (option.selected) {
        optionButton.classList.add("is-selected");
        optionButton.setAttribute("aria-selected", "true");
      }

      syncOptionDisabledState(optionButton, option);

      if (hasSwatches) {
        const optionSwatch = document.createElement("span");
        optionSwatch.className =
          "loco-dropdown__option-swatch" +
          (isMetaSwatch ? " loco-dropdown__option-swatch--meta" : "");

        const color = option.dataset.swatchColor;
        const image = option.dataset.swatchImage;

        if (isMetaSwatch) {
          if (color) optionSwatch.style.setProperty("--option-color", color);
          if (image) {
            optionSwatch.style.setProperty("--option-color-image", `url('${image}')`);
          }
        } else {
          if (color) optionSwatch.style.setProperty("--swatch-color", color);
          if (image) optionSwatch.style.setProperty("--swatch-image", `url('${image}')`);
        }

        optionButton.appendChild(optionSwatch);
      }

      const label = document.createElement("span");
      label.className = "loco-dropdown__option-label";
      label.textContent = option.textContent.trim();
      optionButton.appendChild(label);

      optionButton.addEventListener("click", (event) => {
        event.stopPropagation();
        if (select.options[index]?.disabled) return;
        applyOption(index, { navigate: navigateOnSelect });
      });

      optionButton.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        if (select.options[index]?.disabled) return;
        applyOption(index, { navigate: navigateOnSelect });
      });

      item.appendChild(optionButton);
      list.appendChild(item);
    });

    panel.appendChild(list);
    root.appendChild(panel);

    const valueEl = root.querySelector("[data-loco-dropdown-value]");
    const swatch = root.querySelector("[data-loco-dropdown-swatch]");
    const arrow = root.querySelector(".loco-dropdown__arrow");

    const syncTrigger = () => {
      const option = select.options[select.selectedIndex];
      if (!option) return;

      if (valueEl) {
        valueEl.textContent = option.value;
      }

      if (!swatch) return;

      const color = option.dataset.swatchColor;
      const image = option.dataset.swatchImage;

      if (isMetaSwatch) {
        if (color) swatch.style.setProperty("--option-color", color);
        if (image) {
          swatch.style.setProperty("--option-color-image", `url('${image}')`);
        } else {
          swatch.style.removeProperty("--option-color-image");
        }
        return;
      }

      if (image) {
        swatch.style.setProperty("--swatch-image", `url('${image}')`);
      } else {
        swatch.style.removeProperty("--swatch-image");
      }

      if (color) {
        swatch.style.setProperty("--swatch-color", color);
      }
    };

    const syncPanel = () => {
      list.querySelectorAll(".loco-dropdown__option").forEach((button, index) => {
        const option = select.options[index];
        const selected = index === select.selectedIndex;
        button.classList.toggle("is-selected", selected);
        button.setAttribute("aria-selected", selected ? "true" : "false");
        if (option) syncOptionDisabledState(button, option);
      });
    };

    const close = () => {
      root.classList.remove("is-open");
      trigger.setAttribute("aria-expanded", "false");
      panel.hidden = true;
      arrow?.classList.remove("is-open");
    };

    const open = () => {
      closeAll(root);
      root.classList.add("is-open");
      trigger.setAttribute("aria-expanded", "true");
      panel.hidden = false;
      arrow?.classList.add("is-open");

      list.querySelector(".loco-dropdown__option.is-selected")?.scrollIntoView({
        block: "nearest",
      });
    };

    const applyOption = (index, { closePanel = true, navigate = false } = {}) => {
      if (index < 0 || index >= select.options.length) return;
      if (select.options[index].disabled) return;

      select.selectedIndex = index;
      syncTrigger();
      syncPanel();

      if (closePanel) {
        close();
      }

      select.dispatchEvent(new Event("change", { bubbles: true }));

      const option = select.options[index];
      if (navigate && option?.dataset.productUrl) {
        window.location.href = option.dataset.productUrl;
      }
    };

    trigger.addEventListener("click", (event) => {
      event.stopPropagation();
      if (root.classList.contains("is-open")) {
        close();
      } else {
        open();
      }
    });

    trigger.addEventListener("keydown", (event) => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();

        if (!root.classList.contains("is-open")) {
          open();
          return;
        }

        const delta = event.key === "ArrowDown" ? 1 : -1;
        const nextIndex = findNextEnabledIndex(select, select.selectedIndex, delta);
        applyOption(nextIndex, { closePanel: false, navigate: false });
      }

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        if (root.classList.contains("is-open")) {
          applyOption(select.selectedIndex, { navigate: navigateOnSelect });
        } else {
          open();
        }
      }
    });

    panel.addEventListener("click", (event) => event.stopPropagation());

    if (select.dataset.locoDropdownBound !== "true") {
      select.dataset.locoDropdownBound = "true";
      select.addEventListener("change", () => {
        syncTrigger();
        syncPanel();
      });
    }

    const syncFromNative = () => {
      syncTrigger();
      syncPanel();
    };

    root._locoDropdownSync = syncFromNative;

    syncFromNative();
  }

  function syncFromNative(scope) {
    const container = scope && scope.querySelectorAll ? scope : document;
    container
      .querySelectorAll(`${ROOT_SELECTOR}[data-loco-dropdown-init="true"]`)
      .forEach((root) => root._locoDropdownSync?.());
  }

  function init(scope) {
    const root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll(ROOT_SELECTOR).forEach(initOne);
    bindGlobals();
  }

  window.LocoDropdown = {
    init,
    initOne,
    closeAll,
    syncFromNative,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });
})();
